import {
  clipEnd,
  evaluateAnimatable,
  formatDuration,
  framesToSeconds,
  type Clip,
  type FilmstripInfo,
  type Rational,
  type Waveform,
} from '@ve/core';
import { Layers, Type, VolumeX } from 'lucide-react';
import { memo, useEffect, useRef } from 'react';
import { useMediaRuntime } from '../../media/service.ts';
import type { MediaRuntime } from '../../media/types.ts';
import { tip } from '../../lib/tooltip.ts';

export interface ClipViewProps {
  clip: Clip;
  pxPerFrame: number;
  fps: Rational;
  selected: boolean;
  /** Visible horizontal window (px, lane coordinates) for virtualising heavy content. */
  viewport: { left: number; right: number };
  height: number;
}

const MAX_FILMSTRIP_TILES = 80;

export const ClipView = memo(function ClipView({
  clip,
  pxPerFrame,
  fps,
  selected,
  viewport,
  height,
}: ClipViewProps) {
  const media = useMediaRuntime((s) => ('mediaId' in clip ? s.runtime[clip.mediaId] : undefined));
  const offline = !!media && media.availability !== 'online' && media.availability !== 'loading';
  const left = clip.start * pxPerFrame;
  const width = Math.max(2, clip.duration * pxPerFrame);
  const muted = 'muted' in clip && clip.muted;

  return (
    <div
      className={`clip clip-${clip.type}${selected ? ' selected' : ''}${clip.enabled ? '' : ' disabled'}${offline ? ' offline' : ''}`}
      style={{ left, width, height, ...(clip.type === 'solid' ? { ['--solid' as string]: clip.color } : {}) }}
      data-clip-id={clip.id}
      {...tip(
        `${clip.type === 'text' ? clip.text : clip.name}${clip.enabled ? '' : ' (disabled)'} · ${formatDuration(framesToSeconds(clip.duration, fps))}\n` +
          (offline ? `Media offline: ${media?.message ?? 'file not available'}\n` : '') +
          (clip.type === 'sequence'
            ? 'Drag to move · edges to trim · double-click to open the chapter'
            : 'Drag to move · drag edges to trim (Alt: ripple) · right-click for more'),
      )}
    >
      {clip.type === 'video' && media && (
        <Filmstrip
          clip={clip}
          media={media}
          pxPerFrame={pxPerFrame}
          fps={fps}
          left={left}
          viewport={viewport}
          height={height}
        />
      )}
      {clip.type === 'image' && media?.thumbnailUrl && (
        <div className="clip-image-tiles" style={{ backgroundImage: `url(${media.thumbnailUrl})` }} />
      )}
      {(clip.type === 'audio' || clip.type === 'video') && media?.waveform && (
        <WaveformView
          clip={clip}
          wave={media.waveform}
          fps={fps}
          width={width}
          height={clip.type === 'video' ? 18 : height - 18}
        />
      )}
      {(clip.type === 'audio' || clip.type === 'video' || clip.type === 'sequence') && (
        <VolumeLine clip={clip} width={width} height={height} />
      )}
      <div className="clip-label">
        {clip.type === 'text' && <Type size={11} />}
        {clip.type === 'sequence' && <Layers size={11} />}
        {muted && <VolumeX size={11} />}
        <span>{clip.type === 'text' ? clip.text : clip.name}</span>
      </div>
      {selected && <KeyframeMarks clip={clip} pxPerFrame={pxPerFrame} />}
    </div>
  );
});

function Filmstrip({
  clip,
  media,
  pxPerFrame,
  fps,
  left,
  viewport,
  height,
}: {
  clip: Extract<Clip, { type: 'video' }>;
  media: MediaRuntime;
  pxPerFrame: number;
  fps: Rational;
  left: number;
  viewport: { left: number; right: number };
  height: number;
}) {
  const strip: { url: string; info: FilmstripInfo } | undefined = media.filmstrip;
  if (!strip) {
    return media.thumbnailUrl ? (
      <div className="clip-image-tiles" style={{ backgroundImage: `url(${media.thumbnailUrl})` }} />
    ) : null;
  }
  const { info } = strip;
  const tileH = Math.max(10, height - 18);
  const tileW = (info.tileWidth / info.tileHeight) * tileH;
  const width = clip.duration * pxPerFrame;
  const first = Math.max(0, Math.floor((viewport.left - left) / tileW));
  const last = Math.min(Math.ceil(width / tileW), Math.ceil((viewport.right - left) / tileW));
  const tiles = [];
  for (let i = first; i < last && tiles.length < MAX_FILMSTRIP_TILES; i++) {
    const t = clip.sourceIn + framesToSeconds((i * tileW) / pxPerFrame, fps) * clip.speed;
    const index = Math.min(info.count - 1, Math.max(0, Math.floor(t / info.interval)));
    tiles.push(
      <div
        key={i}
        className="filmstrip-tile"
        style={{
          left: i * tileW,
          width: tileW,
          height: tileH,
          backgroundImage: `url(${strip.url})`,
          backgroundSize: `${info.count * tileW}px ${tileH}px`,
          backgroundPosition: `${-index * tileW}px 0`,
        }}
      />,
    );
  }
  return <div className="filmstrip">{tiles}</div>;
}

function WaveformView({
  clip,
  wave,
  fps,
  width,
  height,
}: {
  clip: Extract<Clip, { type: 'audio' | 'video' }>;
  wave: Waveform;
  fps: Rational;
  width: number;
  height: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawWidth = Math.min(width, 4096);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    drawWaveform(
      canvas,
      wave,
      clip.sourceIn,
      framesToSeconds(clip.duration, fps) * clip.speed,
      drawWidth,
      height,
    );
  }, [wave, clip.sourceIn, clip.duration, clip.speed, fps, drawWidth, height]);
  if (height < 6) return null;
  return <canvas ref={ref} className="clip-waveform" style={{ width, height }} />;
}

function drawWaveform(
  canvas: HTMLCanvasElement,
  wave: Waveform,
  from: number,
  span: number,
  width: number,
  height: number,
) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(width * dpr));
  canvas.height = Math.max(1, Math.round(height * dpr));
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  const mid = height / 2;
  for (let x = 0; x < width; x++) {
    const t0 = from + (x / width) * span;
    const t1 = from + ((x + 1) / width) * span;
    let peak = 0;
    for (let i = Math.floor(t0 * wave.rate); i <= Math.floor(t1 * wave.rate); i++)
      peak = Math.max(peak, wave.peaks[i] ?? 0);
    const h = (peak / 255) * mid;
    ctx.fillRect(x, mid - h, 1, Math.max(1, h * 2));
  }
}

/** Rubber-band style display of the volume curve (0 % at bottom, 200 % at top). */
function VolumeLine({ clip, width, height }: { clip: Clip; width: number; height: number }) {
  const anim = clip.params.volume;
  if (!anim) return null;
  const y = (v: number) => height - 4 - (Math.min(2, v) / 2) * (height - 8);
  let points: string;
  if (anim.keyframes.length === 0) points = `0,${y(anim.value)} ${width},${y(anim.value)}`;
  else {
    const steps = Math.min(200, Math.max(2, Math.round(width / 4)));
    const pts: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const f = (i / steps) * clip.duration;
      pts.push(`${(i / steps) * width},${y(evaluateAnimatable(anim, f))}`);
    }
    points = pts.join(' ');
  }
  return (
    <svg className="clip-volume" width={width} height={height}>
      <polyline points={points} />
    </svg>
  );
}

function KeyframeMarks({ clip, pxPerFrame }: { clip: Clip; pxPerFrame: number }) {
  const frames = new Set<number>();
  for (const anim of Object.values(clip.params)) for (const k of anim?.keyframes ?? []) frames.add(k.frame);
  const end = clipEnd(clip) - clip.start;
  return (
    <div className="clip-keyframes">
      {[...frames]
        .filter((f) => f >= 0 && f <= end)
        .map((f) => (
          <span key={f} className="kf-mark" style={{ left: f * pxPerFrame }} data-kf-frame={f} />
        ))}
    </div>
  );
}
